import { AnalysisType } from 'src/app/analysis/models/AnalysisType';
import { AnalysisCategory } from 'src/app/analysis/models/AnalysisCategory';
import { AnalysisPage } from 'src/app/analysis/models/AnalysisPage';
import { Analysis } from 'src/app/analysis/models/Analysis';
import { AnalysisUtils } from 'src/app/analysis/utils/analysis-utils';
import { NavigationUtils } from './navigation-utils';
import { AngularJSUtils } from '../../analysis/utils/angularjs-utils';

type VersionParam = number | string | null | undefined;

export class HTTPUtils {
    public static readonly OLD_PATH_TO_ANALYSIS: string = '/analysis/#/analysis/single/';
    public static readonly OLD_PATH_GROUP_ANALYSIS: string = '/analysis/#/analysis/group/';
    public static readonly OLD_PATH_SINGLE_FREE_CR13: string = '/analysis/#/analysis/single////FREE//criterion13/';
    public static readonly OLD_PATH_GROUP_CR13: string = '/analysis/#/analysis/group////AVERAGE//criterion13/';
    public static readonly OLD_PATH_SINGLE_SUPPORT_CR13: string = '/analysis/#/analysis/single////SUPPORT//criterion13/';
    public static readonly OLD_PATH_TO_TEMPLATE_ANALYSIS: string = '/analysis/#/analysis/template/';
    public static readonly OLD_HEADER_ANALYSIS_SUFFIX: string = '/////header';
    public static readonly OLD_HEADER_ANALYSIS_SUFFIX_WITH_VERSION: string = '////header';
    public static readonly OLD_HEADER_ANALYSIS_CONFIRM_SUFFIX: string = '////confirm/header';
    public static readonly OLD_PATH_TO_TEMPLATE_TREE: string = '/analysis/#/search/template/tree';
    public static readonly NEW_PATH_TO_ANALYSIS_SINGLE: string = '/ts/#/analysis/single/';
    public static readonly NEW_PATH_TO_ANALYSIS_GROUP: string = '/ts/#/analysis/group/';
    public static readonly NEW_PATH_TO_ANALYSIS_SUPPORT: string = '/ts/#/analysis/support/';
    public static readonly NEW_PATH_TO_TEMPLATE: string = '/ts/#/analysis/template/';
    public static readonly PATH_TO_ANALYSIS_SINGLE: string = 'analysis/single/';
    public static readonly PATH_TO_ANALYSIS_GROUP: string = 'analysis/group/';
    public static readonly PATH_TO_ANALYSIS_PREVIEW: string = '#/analysis/preview/';
    public static readonly PATH_TO_ANALYSIS_SUPPORT: string = 'analysis/support/';
    public static readonly PATH_TO_CR13: string = 'cr13';
    public static readonly PATH_TO_ALL_HAZARDS: string = 'allhazards';
    public static readonly PATH_TO_COUNTERMEASURE: string = 'countermeasures';
    public static readonly PATH_TO_DWSM_VIDEO_PLAYER: string = '/ts/#/dwsm/video-player';
    public static readonly PATH_TO_GROUP_ANALYSIS_NEW_TAB: string = '/ts/#/analysis/group/';

    private static readonly PAGE_ROUTES = ['header', 'overview'];
    private static readonly CRITERION_PATTERN = /^cr\d+/;

    public static isPageRoute(segment: string): boolean {
        return this.PAGE_ROUTES.includes(segment) || this.CRITERION_PATTERN.test(segment);
    }

    public static isOnValidAnalysisPage(url: string): boolean {
        return url.endsWith('/header') || url.includes('/overview') || /\/cr\d+(\/|$)/.test(url);
    }

    public static extractAnalysisTypeFromUrl(url: string): string {
        const segments = url.split('/');
        const newIndex = segments.indexOf('new');
        return newIndex !== -1 && segments.length > newIndex + 1 ? segments[newIndex + 1] : 'FREE';
    }

    public static extractNewAnalysisBaseRoute(url: string): string {
        const segments = url.split('/');
        const newIndex = segments.indexOf('new');

        return newIndex === -1 ? url : segments.slice(0, newIndex + 2).join('/');
    }

    public static isSupportType(analysisType: string | undefined | null): boolean {
        if (!analysisType) return false;
        const typeAsEnum = AnalysisType[analysisType as keyof typeof AnalysisType];
        return AnalysisUtils.supportAnalysisTypes.has(typeAsEnum) || AnalysisUtils.supportAnalysisTypes.has(analysisType as AnalysisType);
    }

    public static isGroupType(analysisType: string | undefined | null): boolean {
        if (!analysisType) return false;
        const typeAsEnum = AnalysisType[analysisType as keyof typeof AnalysisType];
        return AnalysisUtils.groupAnalysisTypes.has(typeAsEnum) || AnalysisUtils.groupAnalysisTypes.has(analysisType as AnalysisType);
    }

    public static getCategoryFromAnalysisType(analysisType: string): AnalysisCategory {
        if (this.isSupportType(analysisType)) {
            return AnalysisCategory.SUPPORT;
        }
        if (this.isGroupType(analysisType)) {
            return AnalysisCategory.GROUP;
        }
        return AnalysisCategory.SINGLE;
    }

    public static getNewAnalysisRouteSegment(analysisType: string): string {
        const category = this.getCategoryFromAnalysisType(analysisType);
        if (category === AnalysisCategory.SUPPORT) {
            return 'SUPPORT';
        }
        if (category === AnalysisCategory.GROUP) {
            return 'AVERAGE';
        }
        return 'FREE';
    }

    public static getPageUrl(page: AnalysisPage): string {
        if (page === AnalysisPage.HEADER) {
            return 'header';
        } else if (page === AnalysisPage.OVERVIEW) {
            return 'overview';
        } else {
            return 'cr' + page.toString() + (page === AnalysisPage.MM13 ? '/' + this.PATH_TO_ALL_HAZARDS : '');
        }
    }

    public static buildAnalysisRouteSegments(analysis: Analysis, page: AnalysisPage, isNewVersion: boolean = false): string[] {
        const pageUrl = this.getPageUrl(page).split('/');
        const analysisKey = analysis.analysisKey ?? '';
        const analysisType = analysis.template ? 'TEMPLATE' : analysis.analysisType?.toString();
        let version = analysis.version ?? 1;
        version = version > 1 && (isNewVersion || analysis.id == null) ? version - 1 : version;

        if (analysisType === 'TEMPLATE') {
            return ['/analysis', 'template', analysis.id?.toString() ?? '', ...pageUrl];
        }

        if (this.isSupportType(analysisType)) {
            return ['/analysis', 'support', analysisKey, version.toString(), ...pageUrl];
        }

        if (this.isGroupType(analysisType)) {
            return ['/analysis', 'group', analysisKey, version.toString(), ...pageUrl];
        }

        return ['/analysis', 'single', analysisKey, version.toString(), ...pageUrl];
    }

    public static openAnalysis(analysisKey: string) {
        NavigationUtils.openUrl(this.getAnalysisURL(analysisKey));
    }

    public static openGroupAnalysis(analysisKey: string) {
        NavigationUtils.openUrl(this.getGroupAnalysisURL(analysisKey));
    }

    public static openGroupAnalysisWithVersion(analysisKey: string, version: number) {
        NavigationUtils.openUrl(this.getGroupAnalysisURLWithVersion(analysisKey, version));
    }

    public static openGroupAnalysisInNewTab(analysisKey: string, version: number | string, page: string = 'header'): void {
        const url = `${this.PATH_TO_GROUP_ANALYSIS_NEW_TAB}${analysisKey}/${version}/${page}`;
        NavigationUtils.openUrlInNewTab(url);
    }

    public static openAnalysisInNewTab(analysisKey: string) {
        NavigationUtils.openUrlInNewTab(this.getAnalysisURL(analysisKey));
    }

    public static openSingleAnalysisWithVersionInNewTab(analysisKey: string, version: number | string): void {
        const route = `/ts${AngularJSUtils.CARDINAL}${this.PATH_TO_ANALYSIS_SINGLE}`;
        NavigationUtils.openUrlInNewTab(`${route}${analysisKey}/${version}/header`);
    }

    public static openGroupAnalysisWithVersionInNewTab(analysisKey: string, version: number | string) {
        const route = `/ts${AngularJSUtils.CARDINAL}${this.PATH_TO_ANALYSIS_GROUP}`;
        NavigationUtils.openUrlInNewTab(`${route}${analysisKey}/${version}/header`);
    }

    public static openSupportAnalysisWithVersionInNewTab(analysisKey: string, version: number | string) {
        const route = `/ts${AngularJSUtils.CARDINAL}${this.PATH_TO_ANALYSIS_SUPPORT}`;
        NavigationUtils.openUrlInNewTab(`${route}${analysisKey}/${version}/header`);
    }

    public static openAnalysisConfirm(analysisKey: string) {
        const route = `/ts${AngularJSUtils.CARDINAL}${this.PATH_TO_ANALYSIS_SINGLE}`;
        NavigationUtils.openUrl(`${route}${analysisKey}/version/header`);
    }

    public static openPreviewAnalysis(analysisKey: string) {
        NavigationUtils.openUrl(this.PATH_TO_ANALYSIS_PREVIEW + analysisKey);
    }

    public static openAnalysisHeaderNew(analysisKey: string, version: VersionParam, analysisType?: AnalysisType): void {
        const path = this.getNewPathFromAnalysisType(analysisType);
        NavigationUtils.openUrl(`${path}${analysisKey}/${version}/header`);
    }

    public static openAnalysisHeaderNewInNewTab(analysisKey: string, version: VersionParam, analysisType?: AnalysisType): void {
        const path = this.getNewPathFromAnalysisType(analysisType);
        NavigationUtils.openUrlInNewTab(`${path}${analysisKey}/${version}/header`);
    }

    public static openAnalysisPageNew(analysisKey: string, version: VersionParam, page: string, analysisType?: AnalysisType): void {
        const path = this.getNewPathFromAnalysisType(analysisType);
        NavigationUtils.openUrl(`${path}${analysisKey}/${version}/${page}`);
    }

    public static openAnalysisPageNewInNewTab(analysisKey: string, version: VersionParam, page: string, analysisType?: AnalysisType): void {
        const path = this.getNewPathFromAnalysisType(analysisType);
        NavigationUtils.openUrlInNewTab(`${path}${analysisKey}/${version}/${page}`);
    }

    public static buildCountermeasureNewTabUrl(
        analysisType: AnalysisType | string,
        analysisKey: string,
        version: VersionParam,
        defMM13ElementId: number | string,
        dataMM13ElementItemId: number | string,
        measureId: number | string
    ): string {
        return `${this.getNewPathFromAnalysisType(analysisType)}${analysisKey}/${version}/${this.PATH_TO_CR13}/${defMM13ElementId}/${
            this.PATH_TO_COUNTERMEASURE
        }/${dataMM13ElementItemId}/${measureId}`;
    }

    public static openCountermeasureInNewTab(
        analysisType: AnalysisType | string,
        analysisKey: string,
        version: VersionParam,
        defMM13ElementId: number | string,
        dataMM13ElementItemId: number | string,
        measureId: number | string
    ): void {
        NavigationUtils.openUrlInNewTab(
            this.buildCountermeasureNewTabUrl(analysisType, analysisKey, version, defMM13ElementId, dataMM13ElementItemId, measureId)
        );
    }

    public static getNewPathFromAnalysisType(analysisType?: AnalysisType | string): string {
        if (!analysisType) {
            return this.NEW_PATH_TO_ANALYSIS_SINGLE;
        }
        const analysisTypeStr = analysisType?.toString();
        if (this.isGroupType(analysisTypeStr) || AnalysisUtils.isGroupAnalysis(analysisType as AnalysisType)) {
            return this.NEW_PATH_TO_ANALYSIS_GROUP;
        }
        if (this.isSupportType(analysisTypeStr) || AnalysisUtils.isSupport(analysisType as AnalysisType)) {
            return this.NEW_PATH_TO_ANALYSIS_SUPPORT;
        }
        return this.NEW_PATH_TO_ANALYSIS_SINGLE;
    }

    public static openTemplateAnalysisNew(analysisId: number | string, page: string = 'header'): void {
        NavigationUtils.openUrl(`${this.NEW_PATH_TO_TEMPLATE}${analysisId}/${page}`);
    }

    public static getPathFromAnalysisType(analysisType: AnalysisType | string) {
        const analysisTypeStr = analysisType?.toString();
        if (this.isGroupType(analysisTypeStr) || AnalysisUtils.isGroupAnalysis(analysisType as AnalysisType)) {
            return this.PATH_TO_ANALYSIS_GROUP;
        }
        if (this.isSupportType(analysisTypeStr) || AnalysisUtils.isSupport(analysisType as AnalysisType)) {
            return this.PATH_TO_ANALYSIS_SUPPORT;
        }
        return this.PATH_TO_ANALYSIS_SINGLE;
    }

    protected static getAnalysisURL(analysisKey: string) {
        return this.OLD_PATH_TO_ANALYSIS + analysisKey + this.OLD_HEADER_ANALYSIS_SUFFIX;
    }

    protected static getGroupAnalysisURL(analysisKey: string) {
        return this.OLD_PATH_GROUP_ANALYSIS + analysisKey + this.OLD_HEADER_ANALYSIS_SUFFIX;
    }

    protected static getGroupAnalysisURLWithVersion(analysisKey: string, version: number) {
        return this.OLD_PATH_GROUP_ANALYSIS + analysisKey + '/' + version + this.OLD_HEADER_ANALYSIS_SUFFIX_WITH_VERSION;
    }

    protected static getAnalysisConfirmURL(analysisKey: string) {
        return this.OLD_PATH_TO_ANALYSIS + analysisKey + this.OLD_HEADER_ANALYSIS_CONFIRM_SUFFIX;
    }

    public static downloadFile(data: ArrayBuffer, fileName: string, fileType: string) {
        const blob = new Blob([data], {
            type: fileType
        });
        const url = globalThis.URL.createObjectURL(blob);
        let download = document.createElement('a');
        download.href = url;
        download.download = fileName;
        download.click();
        globalThis.URL.revokeObjectURL(url);
    }

    public static calculateTotalSizeInMB(totalSizeInBytes: number): number {
        return Number((totalSizeInBytes / (1024 * 1024)).toFixed(2));
    }
}
